import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { getSupabaseClient } from "../../../lib/supabaseClient";

// ─── Types ────────────────────────────────────────────────────────────────────

type AppStatus = "pending" | "approved" | "rejected";

interface PhotographerApplication {
  id: string;
  name: string;
  email: string;
  line_or_contact: string | null;
  service_cities: string[] | null;
  languages: string[] | null;
  portfolio_url: string | null;
  profile_image_url: string | null;
  short_bio: string | null;
  status: AppStatus;
  created_at: string;
  reviewed_at: string | null;
  reviewer_note: string | null;
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<AppStatus, string> = {
  pending: "bg-amber-50 text-amber-600",
  approved: "bg-emerald-50 text-emerald-600",
  rejected: "bg-red-50 text-red-500",
};

const STATUS_LABELS: Record<AppStatus, string> = {
  pending: "待審核",
  approved: "已核准",
  rejected: "已拒絕",
};

function AppStatusBadge({ status }: { status: AppStatus }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${STATUS_STYLES[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

// ─── Chip list ────────────────────────────────────────────────────────────────

function ChipList({ items, color = "bg-[#C8B8A6]/15 text-[#2C2C2C]/60" }: { items: string[]; color?: string }) {
  if (!items?.length) return <span className="text-[#2C2C2C]/30 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <span key={item} className={`px-2 py-0.5 rounded-full text-xs font-light uppercase ${color}`}>{item}</span>
      ))}
    </div>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function DetailModal({
  app,
  onClose,
  onApprove,
  onReject,
}: {
  app: PhotographerApplication;
  onClose: () => void;
  onApprove: (app: PhotographerApplication) => void;
  onReject: (app: PhotographerApplication) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-sm max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#D9D9D9]/50 shrink-0">
          <div>
            <h2 className="text-base font-light text-[#2C2C2C]">申請詳情</h2>
            <p className="text-xs text-[#2C2C2C]/40 mt-0.5">{app.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <AppStatusBadge status={app.status} />
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">姓名</p>
              <p className="text-sm text-[#2C2C2C] font-light">{app.name}</p>
            </div>
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">電子郵件</p>
              <p className="text-sm text-[#2C2C2C] font-light">{app.email}</p>
            </div>
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">LINE / 聯絡方式</p>
              <p className="text-sm text-[#2C2C2C] font-light">{app.line_or_contact || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">申請時間</p>
              <p className="text-sm text-[#2C2C2C] font-light">{new Date(app.created_at).toLocaleDateString("zh-TW")}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-2">可服務城市</p>
            <ChipList items={app.service_cities ?? []} />
          </div>

          <div>
            <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-2">可服務語言</p>
            <ChipList items={app.languages ?? []} />
          </div>

          {app.portfolio_url && (
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">作品集</p>
              <a
                href={app.portfolio_url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sm text-[#C8B8A6] hover:underline break-all cursor-pointer"
              >
                {app.portfolio_url}
              </a>
            </div>
          )}

          {app.profile_image_url && (
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">個人照片</p>
              <a
                href={app.profile_image_url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sm text-[#C8B8A6] hover:underline break-all cursor-pointer"
              >
                {app.profile_image_url}
              </a>
            </div>
          )}

          {app.short_bio && (
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">自我介紹</p>
              <p className="text-sm text-[#2C2C2C] font-light leading-relaxed bg-[#F7F6F3] rounded-lg px-4 py-3">
                {app.short_bio}
              </p>
            </div>
          )}

          {app.reviewer_note && (
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1">審核備註</p>
              <p className="text-sm text-[#2C2C2C] font-light leading-relaxed bg-red-50 rounded-lg px-4 py-3">
                {app.reviewer_note}
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {app.status === "pending" && (
          <div className="px-6 py-4 border-t border-[#D9D9D9]/50 flex justify-end gap-3 shrink-0">
            <button
              onClick={() => onReject(app)}
              className="px-4 py-2 rounded-md border border-[#D9D9D9] text-xs text-[#2C2C2C]/60 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-close-circle-line" />
                </div>
                拒絕申請
              </div>
            </button>
            <button
              onClick={() => onApprove(app)}
              className="px-5 py-2 rounded-md bg-[#2C2C2C] text-xs text-white hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-check-line" />
                </div>
                核准 &amp; 建立攝影師帳號
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Reject Dialog ────────────────────────────────────────────────────────────

function RejectDialog({
  app,
  onConfirm,
  onCancel,
  saving,
}: {
  app: PhotographerApplication;
  onConfirm: (note: string) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20">
      <div className="bg-white rounded-lg w-full max-w-md shadow-sm p-6">
        <h3 className="text-base font-light text-[#2C2C2C] mb-1">拒絕此申請</h3>
        <p className="text-xs text-[#2C2C2C]/50 mb-4">{app.name} — {app.email}</p>
        <FormField label="審核備註（選填，申請人不會看到）">
          <textarea
            className={`${inputCls} h-24 resize-none`}
            value={note}
            onChange={(e) => { if (e.target.value.length <= 500) setNote(e.target.value); }}
            maxLength={500}
            placeholder="說明拒絕原因，僅供內部記錄..."
          />
          <p className="text-xs text-[#2C2C2C]/30 text-right mt-0.5">{note.length}/500</p>
        </FormField>
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-md border border-[#D9D9D9] text-xs text-[#2C2C2C]/60 hover:border-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={() => onConfirm(note)}
            disabled={saving}
            className="px-5 py-2 rounded-md bg-red-500 text-xs text-white hover:bg-red-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving ? "處理中..." : "確認拒絕"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Approve Dialog ───────────────────────────────────────────────────────────

function ApproveDialog({
  app,
  onConfirm,
  onCancel,
  saving,
}: {
  app: PhotographerApplication;
  onConfirm: (code: string) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [code, setCode] = useState(`PH-${Date.now().toString().slice(-5)}`);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20">
      <div className="bg-white rounded-lg w-full max-w-md shadow-sm p-6">
        <h3 className="text-base font-light text-[#2C2C2C] mb-1">核准申請</h3>
        <p className="text-xs text-[#2C2C2C]/50 mb-4">
          核准後將在攝影師管理中建立正式帳號：{app.name}
        </p>
        <FormField label="攝影師代碼 (photographer_code)" required>
          <input
            className={inputCls}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="KY-001"
          />
        </FormField>
        <p className="text-xs text-[#2C2C2C]/40 mt-2 leading-relaxed">
          建立後可在攝影師管理頁進一步編輯詳細資料。
        </p>
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-md border border-[#D9D9D9] text-xs text-[#2C2C2C]/60 hover:border-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={() => onConfirm(code)}
            disabled={saving || !code.trim()}
            className="px-5 py-2 rounded-md bg-[#2C2C2C] text-xs text-white hover:bg-[#2C2C2C]/80 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving ? "建立中..." : "確認核准"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type FilterStatus = "all" | AppStatus;

export default function AdminPhotographerApplicationsPage() {
  const t = useAdminT();
  const toast = useToast();

  const [applications, setApplications] = useState<PhotographerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");

  const [detailApp, setDetailApp] = useState<PhotographerApplication | null>(null);
  const [rejectApp, setRejectApp] = useState<PhotographerApplication | null>(null);
  const [approveApp, setApproveApp] = useState<PhotographerApplication | null>(null);
  const [actionSaving, setActionSaving] = useState(false);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { data, error: err } = await supabase
        .from("photographer_applications")
        .select("*")
        .order("created_at", { ascending: false });
      if (err) throw err;
      setApplications((data ?? []) as PhotographerApplication[]);
    } catch (e) {
      setError((e as Error).message ?? "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const filtered = applications.filter((a) => filterStatus === "all" || a.status === filterStatus);

  const counts: Record<FilterStatus, number> = {
    all: applications.length,
    pending: applications.filter((a) => a.status === "pending").length,
    approved: applications.filter((a) => a.status === "approved").length,
    rejected: applications.filter((a) => a.status === "rejected").length,
  };

  // ── Reject ────────────────────────────────────────────────────────────────

  const handleReject = async (note: string) => {
    if (!rejectApp) return;
    setActionSaving(true);
    try {
      const supabase = getSupabaseClient();
      const { error: err } = await supabase
        .from("photographer_applications")
        .update({
          status: "rejected",
          reviewer_note: note || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", rejectApp.id);
      if (err) throw err;
      toast.success("已拒絕申請");
      setRejectApp(null);
      setDetailApp(null);
      await fetchApplications();
    } catch (e) {
      toast.error("操作失敗，請重試");
    } finally {
      setActionSaving(false);
    }
  };

  // ── Approve ───────────────────────────────────────────────────────────────

  const handleApprove = async (photographerCode: string) => {
    if (!approveApp) return;
    setActionSaving(true);
    try {
      const supabase = getSupabaseClient();

      // Build photographer record from application
      const newPhotographer = {
        photographer_code: photographerCode.trim(),
        display_name: approveApp.name,
        legal_name: approveApp.name,
        email: approveApp.email,
        phone: "",
        country_code: "JP",
        city_base: (approveApp.service_cities ?? [])[0] ?? "",
        languages: approveApp.languages ?? [],
        status: "active",
        portfolio_url: approveApp.portfolio_url ?? "",
        notes: approveApp.short_bio
          ? `申請備註：${approveApp.short_bio}`
          : `由申請 ${approveApp.id} 核准建立`,
        is_active: true,
      };

      const { error: insertErr } = await supabase.from("photographers").insert(newPhotographer);
      if (insertErr) throw insertErr;

      // Update application status
      const { error: updateErr } = await supabase
        .from("photographer_applications")
        .update({
          status: "approved",
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", approveApp.id);
      if (updateErr) throw updateErr;

      toast.success("核准成功，攝影師帳號已建立");
      setApproveApp(null);
      setDetailApp(null);
      await fetchApplications();
    } catch (e) {
      toast.error(`操作失敗：${(e as Error).message}`);
    } finally {
      setActionSaving(false);
    }
  };

  const FILTER_TABS: { key: FilterStatus; label: string }[] = [
    { key: "all", label: `全部 (${counts.all})` },
    { key: "pending", label: `待審核 (${counts.pending})` },
    { key: "approved", label: `已核准 (${counts.approved})` },
    { key: "rejected", label: `已拒絕 (${counts.rejected})` },
  ];

  const headers = ["申請人", "可服務城市", "作品集", "申請時間", "狀態", "操作"];

  return (
    <AdminLayout
      title="攝影師申請審核"
      subtitle="審核封測攝影師申請 — 核准後自動建立正式攝影師帳號"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${
                filterStatus === tab.key
                  ? "bg-white text-[#2C2C2C]"
                  : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={fetchApplications}
          className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer"
          title="重新整理"
        >
          <i className="ri-refresh-line text-sm" />
        </button>
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchApplications} />
      ) : (
        <AdminTable
          headers={headers}
          empty={filtered.length === 0}
          emptyMessage="尚無符合條件的申請"
        >
          {filtered.map((app) => (
            <tr
              key={app.id}
              className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors cursor-pointer"
              onClick={() => setDetailApp(app)}
            >
              <td className="px-5 py-3">
                <div>
                  <p className="text-sm font-light text-[#2C2C2C]">{app.name}</p>
                  <p className="text-xs text-[#2C2C2C]/40 mt-0.5">{app.email}</p>
                </div>
              </td>
              <td className="px-5 py-3">
                <ChipList items={app.service_cities ?? []} />
              </td>
              <td className="px-5 py-3">
                {app.portfolio_url ? (
                  <a
                    href={app.portfolio_url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-[#C8B8A6] hover:underline truncate max-w-[180px] block cursor-pointer"
                  >
                    {app.portfolio_url}
                  </a>
                ) : (
                  <span className="text-xs text-[#2C2C2C]/30">—</span>
                )}
              </td>
              <td className="px-5 py-3 text-xs text-[#2C2C2C]/50">
                {new Date(app.created_at).toLocaleDateString("zh-TW")}
              </td>
              <td className="px-5 py-3">
                <AppStatusBadge status={app.status} />
              </td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setDetailApp(app)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer"
                    title="查看詳情"
                  >
                    <i className="ri-eye-line text-sm" />
                  </button>
                  {app.status === "pending" && (
                    <>
                      <button
                        onClick={() => { setApproveApp(app); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-emerald-500 hover:bg-emerald-50 transition-colors cursor-pointer"
                        title="核准"
                      >
                        <i className="ri-check-line text-sm" />
                      </button>
                      <button
                        onClick={() => { setRejectApp(app); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-red-400 hover:bg-red-50 transition-colors cursor-pointer"
                        title="拒絕"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </AdminTable>
      )}

      {/* Detail modal */}
      {detailApp && !rejectApp && !approveApp && (
        <DetailModal
          app={detailApp}
          onClose={() => setDetailApp(null)}
          onApprove={(app) => { setApproveApp(app); }}
          onReject={(app) => { setRejectApp(app); }}
        />
      )}

      {/* Reject dialog */}
      {rejectApp && (
        <RejectDialog
          app={rejectApp}
          onConfirm={handleReject}
          onCancel={() => setRejectApp(null)}
          saving={actionSaving}
        />
      )}

      {/* Approve dialog */}
      {approveApp && (
        <ApproveDialog
          app={approveApp}
          onConfirm={handleApprove}
          onCancel={() => setApproveApp(null)}
          saving={actionSaving}
        />
      )}
    </AdminLayout>
  );
}
